"use client";

import * as React from "react";
import {
  useEditor,
  EditorContent,
  type AnyExtension,
  type Editor,
  type JSONContent,
} from "@tiptap/react";
import type { EditorView } from "@tiptap/pm/view";
import { cn } from "../../utils";
import { usePublicIdHrefResolver } from "../../lib/public-id-link-context";
import {
  CHIP_BASE_CSS,
  EDITOR_MARKDOWN_CSS,
  useChipTypeCss,
  useInjectedStyles,
  type ChipStylesConfig,
} from "./styles";
import { buildChipExtensions } from "./block-extensions";
import {
  CHIP_REF_CLASS,
  CHIP_REF_NODE,
  chipNodeMeta,
  type ChipMarkdownSpec,
  type ChipNodeMeta,
  type ChipRefAttrs,
} from "./chip-extension";
import { createChipStateExtension, type ChipStateGetter } from "./chip-state";
import { CHIP_DOC_MAX_DEPTH } from "@quovy/types/chip-doc/doc-depth";
import { chipDocFromText, chipTextFromEditor } from "./text-value";

export {
  createChipExtension,
  chipAttrsOf,
  chipNodeMeta,
  CHIP_REF_CLASS,
  CHIP_REF_NODE,
  CHIP_TYPE_ATTR,
  type ChipExtensionOptions,
  type ChipMarkdownSpec,
  type ChipNodeMeta,
  type ChipRefAttrs,
} from "./chip-extension";
export { createChipStateExtension, type ChipStateGetter, type ChipVisualState } from "./chip-state";
export { chipDocFromText, chipTextFromEditor } from "./text-value";
export { CHIP_DOC_MAX_DEPTH } from "@quovy/types/chip-doc/doc-depth";
export {
  ChipPopover,
  ChipPopoverHeader,
  useChipTarget,
  type ChipInteractionProps,
  type ChipPopoverProps,
  type ChipPopoverHeaderProps,
  type ChipPopoverTriggerProps,
  type ChipTarget,
  type ChipTargetTrigger,
  type UseChipTargetOptions,
  type UseChipTargetResult,
} from "./chip-popover";
export {
  createChipSuggestion,
  type ChipListHandle,
  type ChipSuggestionConfig,
} from "./chip-suggestion";
export {
  useSuggestionListNav,
  SuggestionListContainer,
  SuggestionRow,
  type UseSuggestionListNavResult,
  type SuggestionListContainerProps,
  type SuggestionRowProps,
} from "./suggestion-list";
export {
  CHIP_BASE_CSS,
  chipTypeCss,
  iconMaskUrl,
  useInjectedStyles,
  type ChipStylesConfig,
  type ChipTypeStyle,
} from "./styles";
export {
  VariableChipList,
  createVariableChipExtension,
  slugifyVariableKey,
  VARIABLE_CHIP_STYLES,
  VARIABLE_REF_TYPE,
  type TemplateVariable,
} from "./variable-chips";
export { buildChipExtensions, type BuildChipExtensionsOptions } from "./block-extensions";
export { SLASH_ITEMS, filterSlashItems, slashShortcutGlyphs, type SlashItem } from "./slash-items";
export {
  PublicIdLinkDecoration,
  publicIdLinkPluginKey,
  type PublicIdLinkResolverRef,
} from "./public-id-link-decoration";

// ============================================================================
// Shared helpers
// ============================================================================

/** Parse a value into Tiptap JSON, or null when it's plain text / invalid. */
export function parseChipDoc(value: string | null | undefined): JSONContent | null {
  if (typeof value !== "string" || !value.trim().startsWith("{")) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null ? (parsed as JSONContent) : null;
  } catch {
    return null;
  }
}

/**
 * Rewrite every chip of `refType` whose id is `oldId` to `newId` (+ label) in a
 * chip doc. Chip ids are how a chip resolves against its backing declaration
 * (a template field's key, a port's name), so renaming the declaration must
 * rename its chips or every reference orphans. Returns the doc unchanged when
 * nothing matches (or it isn't a chip doc).
 *
 * Depth-capped like every other chip-doc walker (see doc-depth.ts): this runs
 * on persisted documents, which are untrusted.
 */
export function renameChipRefs(
  doc: string,
  opts: { refType: string; oldId: string; newId: string; newLabel: string },
): string {
  const root = parseChipDoc(doc);
  if (!root) return doc;
  let changed = false;
  const walk = (
    node: {
      type?: string | undefined;
      attrs?: Record<string, unknown> | undefined;
      content?: unknown;
    },
    depth: number,
  ) => {
    if (depth > CHIP_DOC_MAX_DEPTH) return;
    if (
      node.type === CHIP_REF_NODE &&
      node.attrs?.refType === opts.refType &&
      node.attrs.id === opts.oldId
    ) {
      node.attrs = { ...node.attrs, id: opts.newId, label: opts.newLabel };
      changed = true;
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content as { type?: string }[]) walk(child, depth + 1);
    }
  };
  walk(root, 0);
  return changed ? JSON.stringify(root) : doc;
}

/** Content to feed Tiptap: parsed JSON when present, else the raw string. */
function initialContent(value: string | null | undefined): JSONContent | string {
  return parseChipDoc(value) ?? value ?? "";
}

/**
 * What a surface's `value` string holds — Tiptap JSON (the default), a markdown
 * string, or plain text.
 *
 * - `"json"` — stringified `getJSON()`; the richest form, and the only one that
 *   can hold marks a family's text form can't spell.
 * - `"markdown"` — tiptap-markdown owns both directions: the value parses as
 *   markdown on load and `getMarkdown()` serializes it back, with chips
 *   crossing through their family's {@link ChipMarkdownSpec}.
 * - `"text"` — literal text, one paragraph per line, no markdown pipeline at
 *   all. Pick this over `"markdown"` whenever the stored string is *not*
 *   markdown (workflow node config: email subjects/bodies, task titles,
 *   prompts): markdown would read `-`, `#` and `*` as syntax on load and
 *   re-escape them on save, rewriting strings the user never edited. Requires
 *   `chipTextSpec`.
 */
export type ChipValueFormat = "json" | "markdown" | "text";

/** tiptap-markdown's storage entry; its types aren't merged into Tiptap's. */
function markdownStorage(editor: Editor): { getMarkdown(): string } | undefined {
  return (editor.storage as unknown as Record<string, { getMarkdown(): string } | undefined>)
    .markdown;
}

// ============================================================================
// Chip interaction (click + visual state) — shared by editor and renderer
// ============================================================================

/** Wire names of the default chip family; stable identity for the props default. */
const DEFAULT_CHIP_NODE = chipNodeMeta();

/** Called when the user clicks or hovers a chip. */
export type ChipClickHandler = (attrs: ChipRefAttrs, element: HTMLElement) => void;

/** Read a rendered chip element's `data-ref-*` attributes back into attrs. */
function attrsOfChipElement(chip: HTMLElement): ChipRefAttrs {
  const id = chip.getAttribute("data-ref-id") ?? "";
  return {
    id,
    label: chip.getAttribute("data-ref-label") ?? id,
    refType: chip.getAttribute("data-ref-type") ?? "",
    field: chip.getAttribute("data-ref-field") || null,
  };
}

/**
 * Ref-backed chip interaction plumbing: a stable chip-state extension reading
 * the latest getter, plus wrapper click/hover handlers that resolve the chip
 * element under the pointer back into its attrs. Identity changes to any
 * callback never rebuild the editor.
 *
 * Hover is delegated with mouseover/mouseout (which bubble) rather than
 * mouseenter/mouseleave (which don't) — chips are recreated by ProseMirror on
 * every doc change, so per-chip listeners would have to be rebound constantly.
 * `hoveredRef` collapses the resulting stream of events into one enter and one
 * leave per chip.
 */
function useChipInteraction(
  handlers: {
    onChipClick?: ChipClickHandler | undefined;
    onChipHover?: ChipClickHandler | undefined;
    onChipHoverEnd?: (() => void) | undefined;
  },
  getChipState: ChipStateGetter | undefined,
  chipNode: ChipNodeMeta,
) {
  const handlersRef = React.useRef(handlers);
  handlersRef.current = handlers;
  const getChipStateRef = React.useRef(getChipState);
  getChipStateRef.current = getChipState;

  const { name: chipNodeName, typeAttr: chipTypeAttr } = chipNode;
  const chipStateExtension = React.useMemo(
    () =>
      createChipStateExtension(() => getChipStateRef.current, {
        name: chipNodeName,
        typeAttr: chipTypeAttr,
      }),
    [chipNodeName, chipTypeAttr],
  );

  /** The chip under the pointer within this surface, or null. */
  const chipFrom = (event: React.MouseEvent<HTMLElement>): HTMLElement | null => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>(`.${CHIP_REF_CLASS}`);
    return chip && event.currentTarget.contains(chip) ? chip : null;
  };

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const handler = handlersRef.current.onChipClick;
    if (!handler) return;
    const chip = chipFrom(event);
    if (!chip) return;
    handler(attrsOfChipElement(chip), chip);
  }, []);

  const hoveredRef = React.useRef<HTMLElement | null>(null);
  const handleMouseOver = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const handler = handlersRef.current.onChipHover;
    if (!handler) return;
    const chip = chipFrom(event);
    if (!chip || chip === hoveredRef.current) return;
    hoveredRef.current = chip;
    handler(attrsOfChipElement(chip), chip);
  }, []);
  const handleMouseOut = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const hovered = hoveredRef.current;
    if (!hovered) return;
    // Moving within the same chip (its text node ⇄ the chip box) fires
    // mouseout too; only a pointer that has actually left it counts.
    const next = event.relatedTarget as Node | null;
    if (next && hovered.contains(next)) return;
    hoveredRef.current = null;
    handlersRef.current.onChipHoverEnd?.();
  }, []);

  /**
   * Enter / Space opens the SELECTED chip, so the popover is reachable without
   * a pointer. Chips are atoms: arrowing onto one gives it a ProseMirror node
   * selection (and the `ProseMirror-selectednode` class), and Enter on a
   * selected atom would otherwise replace it — so this is protective as well
   * as additive. Read off the DOM rather than the editor state because this
   * plumbing is deliberately editor-instance-free; the class is ProseMirror's
   * own and is set on exactly the node the keystroke would have acted on.
   */
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const handler = handlersRef.current.onChipClick;
    if (!handler) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    const chip = event.currentTarget.querySelector<HTMLElement>(
      `.${CHIP_REF_CLASS}.ProseMirror-selectednode`,
    );
    if (!chip) return;
    event.preventDefault();
    handler(attrsOfChipElement(chip), chip);
  }, []);

  return { chipStateExtension, handleClick, handleMouseOver, handleMouseOut, handleKeyDown };
}

/** Dispatches a no-op transaction when `key` changes so chip-state decorations
 *  re-evaluate against the getter's fresh data. */
function useChipStateRefresh(editor: Editor | null, key: unknown): void {
  React.useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta("chipStateRefresh", true));
  }, [editor, key]);
}

// ============================================================================
// Editor
// ============================================================================

export type EditorVariant = "boxed" | "seamless";

export interface RichChipEditorProps {
  /** Current value — Tiptap JSON string or plain text (legacy), or a markdown
   *  string when `valueFormat` is `"markdown"`. */
  value?: string | null | undefined;
  /** Called with the re-serialized value on every edit, in `valueFormat`. */
  onChange?: (value: string) => void;
  /** Called with the current serialized value when the editor loses focus. */
  onBlur?: (value: string) => void;
  onFocus?: () => void;
  /** What `value`/`onChange` carry (default `"json"`); see
   *  {@link ChipValueFormat}. `"markdown"` implies `markdown`, `"text"`
   *  forces it off. */
  valueFormat?: ChipValueFormat;
  /** Chip ⇄ tag-text conversion for `valueFormat="text"` (the family's own
   *  {@link ChipMarkdownSpec} — the same spelling either format uses).
   *  Ignored by the other formats. */
  chipTextSpec?: ChipMarkdownSpec;
  /** Placeholder text when the editor is empty. */
  placeholder?: string;
  /** Accessible name for the textbox (defaults to `placeholder`). */
  ariaLabel?: string;
  /** Associates a visible `<Label htmlFor>` with the editor surface. */
  id?: string;
  /** Marks the editor surface invalid. */
  ariaInvalid?: boolean;
  /** Points the editor surface at the message describing why it is invalid. */
  ariaDescribedBy?: string;
  /** Disable editing. */
  disabled?: boolean;
  /** When false, the document still renders but is not editable. */
  editable?: boolean;
  /** Editor min-height in rows (default: 8). Ignored when `singleLine` or `seamless`. */
  rows?: number;
  /** Cap on visible rows; content past it scrolls. Ignored when `singleLine`. */
  maxRows?: number;
  /** Single-line mode: no block nodes, Enter swallowed. */
  singleLine?: boolean;
  /**
   * `boxed` is a form control (border, min-height).
   * `seamless` is the Linear-style surface: no chrome, so focusing it only
   * adds a caret. Layout must match compact MarkdownRenderer.
   */
  variant?: EditorVariant;
  /** Parse plain-text/pasted content as markdown (default true). */
  markdown?: boolean;
  /** Linear issue markdown (`*` lists, no autolink). Chip docs leave this off. */
  linearMarkdown?: boolean;
  /** Extra attributes on the Link mark. */
  linkHTMLAttributes?: Record<string, string>;
  /** Follow autolinked URLs on click. Linear markdown turns this on. */
  openLinksOnClick?: boolean;
  /** Decorate bare public ids as links (view-only; does not rewrite the value). */
  publicIdLinks?: boolean;
  /** Additional className for the wrapper. */
  className?: string;
  /**
   * Configured chip node (from {@link createChipExtension}). Memoize at the
   * call site — a new instance rebuilds the editor. Omit for markdown-only
   * surfaces (task description).
   */
  chipExtension?: AnyExtension;
  /** Wire names of `chipExtension` — pass the same options' {@link chipNodeMeta}
   *  whenever the family overrides the node name or type attribute. */
  chipNode?: ChipNodeMeta;
  /** Per-refType chip styling for this surface's chip family. */
  chipStyles?: ChipStylesConfig;
  /** Called when the user presses Cmd+Enter (Mac) or Ctrl+Enter. */
  onSubmit?: () => void;
  /**
   * Apply parent `value` changes even while the editor is focused. The comment
   * composer clears its draft on submit without blurring (⌘ Enter), and a
   * failed submit puts that draft back. Off by default so a focused editor is
   * not reset by a stale parent value.
   */
  syncWhileFocused?: boolean;
  /** Receives the live Tiptap editor, for chrome that has to insert at the
   *  caret (e.g. a "pick a variable" popover next to the surface). Null until
   *  the editor mounts (`immediatelyRender: false`) and after it unmounts. */
  editorRef?: React.MutableRefObject<Editor | null>;
  /** Optional paste interceptor (runs before link/markdown paste handling). */
  handlePaste?: (view: EditorView, event: ClipboardEvent) => boolean;
  /** Called when the user clicks a chip (chips get a pointer cursor). What
   *  opens — a {@link ChipPopover}, a drawer, ... — is up to the caller. */
  onChipClick?: ChipClickHandler;
  /** Called when the pointer enters a chip. Pair with `onChipHoverEnd`; both
   *  come from `useChipTarget({ trigger: "hover" })` as `chipProps`. */
  onChipHover?: ChipClickHandler;
  /** Called when the pointer leaves the hovered chip. */
  onChipHoverEnd?: () => void;
  /** Per-chip visual state (e.g. `{ invalid: true }` → red + warning icon).
   *  Read through a ref, so pass `chipStateKey` to re-evaluate on data changes. */
  getChipState?: ChipStateGetter;
  /** Bump when the data behind `getChipState` changes (e.g. a serialized key
   *  list) — forces the decorations to re-evaluate. */
  chipStateKey?: unknown;
}

/** Shared TipTap editor. Chip families are optional; `seamless` is caret-only. */
export function RichChipEditor({
  value,
  onChange,
  onBlur,
  onFocus,
  valueFormat = "json",
  chipTextSpec,
  placeholder,
  ariaLabel,
  id,
  ariaInvalid,
  ariaDescribedBy,
  disabled,
  editable,
  rows = 8,
  maxRows,
  singleLine = false,
  variant = "boxed",
  markdown = true,
  linearMarkdown = false,
  linkHTMLAttributes,
  openLinksOnClick,
  publicIdLinks = false,
  className,
  chipExtension,
  chipNode = DEFAULT_CHIP_NODE,
  chipStyles,
  onSubmit,
  syncWhileFocused = false,
  editorRef,
  handlePaste,
  onChipClick,
  onChipHover,
  onChipHoverEnd,
  getChipState,
  chipStateKey,
}: RichChipEditorProps) {
  const markdownValue = valueFormat === "markdown";
  const textValue = valueFormat === "text";
  const seamless = variant === "seamless";
  const hasChip = Boolean(chipExtension);
  const isEditable = (editable ?? true) && !disabled;
  const lastValueRef = React.useRef<string>(value ?? "");
  const focusedRef = React.useRef(false);
  const valueRef = React.useRef(value ?? "");
  valueRef.current = value ?? "";
  // TipTap emits an empty doc on create; ignore it until the stored value is applied.
  const acceptEditsRef = React.useRef(false);
  const liveEditorRef = React.useRef<Editor | null>(null);
  const chipTextSpecRef = React.useRef(chipTextSpec);
  chipTextSpecRef.current = chipTextSpec;
  const onSubmitRef = React.useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const { chipStateExtension, handleClick, handleMouseOver, handleMouseOut, handleKeyDown } =
    useChipInteraction({ onChipClick, onChipHover, onChipHoverEnd }, getChipState, chipNode);

  const publicIdResolver = usePublicIdHrefResolver();
  const publicIdLinkResolver = React.useRef(publicIdResolver);
  publicIdLinkResolver.current = publicIdResolver;

  const serialize = React.useCallback(
    (ed: Editor): string => {
      if (textValue) return chipTextFromEditor(ed, chipTextSpecRef.current, chipNode);
      if (markdownValue) return markdownStorage(ed)?.getMarkdown() ?? "";
      return JSON.stringify(ed.getJSON());
    },
    [textValue, markdownValue, chipNode],
  );

  const writeValue = React.useCallback(
    (ed: Editor, incoming: string, emitUpdate: boolean) => {
      if (!incoming) {
        ed.commands.clearContent(emitUpdate);
        return;
      }
      const content = textValue
        ? chipDocFromText(incoming, chipTextSpecRef.current, chipNode)
        : markdownValue
          ? incoming
          : (parseChipDoc(incoming) ?? incoming);
      ed.commands.setContent(content, { emitUpdate });
    },
    [textValue, markdownValue, chipNode],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      ...buildChipExtensions({
        ...(chipExtension ? { chip: chipExtension } : {}),
        placeholder: placeholder ?? "",
        singleLine,
        markdown: !textValue && (markdown || markdownValue),
        plainText: textValue,
        ...(linearMarkdown ? { linear: true } : {}),
        ...(linkHTMLAttributes ? { linkHTMLAttributes } : {}),
        ...(openLinksOnClick !== undefined ? { openLinksOnClick } : {}),
        ...(publicIdLinks ? { publicIdLinkResolver } : {}),
      }),
      chipStateExtension,
    ],
    content: textValue
      ? chipDocFromText(value ?? "", chipTextSpec, chipNode)
      : markdownValue
        ? (value ?? "")
        : initialContent(value),
    editable: isEditable,
    onUpdate({ editor: ed, transaction }) {
      if (!acceptEditsRef.current) return;
      // setEditable emits `update` without changing the document, which would write the current text back over a draft the parent just cleared.
      if (transaction && !transaction.docChanged) return;
      const serialized = serialize(ed);
      lastValueRef.current = serialized;
      onChange?.(serialized);
    },
    onFocus() {
      focusedRef.current = true;
      onFocus?.();
    },
    onBlur({ editor: ed }) {
      focusedRef.current = false;
      onBlur?.(serialize(ed));
    },
    editorProps: {
      attributes: {
        class: "max-w-none focus:outline-none",
        "data-1p-ignore": "true",
        // contenteditable has no implicit textbox role; axe flags it without both.
        role: "textbox",
        "aria-multiline": singleLine ? "false" : "true",
        "aria-label": ariaLabel ?? placeholder ?? "Rich text input",
        ...(id ? { id } : {}),
        ...(ariaInvalid ? { "aria-invalid": "true" } : {}),
        ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
      },
      handleKeyDown(_view, event) {
        const ed = liveEditorRef.current;
        if (event.key === "Escape") {
          if (document.querySelector(".slash-command-popup")) return false;
          if (!ed) return false;
          const incoming = valueRef.current;
          if (incoming !== serialize(ed)) {
            writeValue(ed, incoming, false);
            lastValueRef.current = incoming;
          }
          ed.commands.blur();
          return true;
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          if (onSubmitRef.current) {
            event.preventDefault();
            onSubmitRef.current();
            return true;
          }
          ed?.commands.blur();
          return true;
        }
        return false;
      },
      handlePaste(view, event) {
        if (handlePaste?.(view, event)) return true;
        if (!singleLine || !event.clipboardData) return false;
        const text = event.clipboardData.getData("text/plain").replace(/(?:\r\n?|\n)+/g, " ");
        view.dispatch(view.state.tr.insertText(text));
        return true;
      },
    },
  });
  liveEditorRef.current = editor;

  React.useEffect(() => {
    if (!editorRef) return;
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);

  React.useEffect(() => {
    editor?.setEditable(isEditable);
  }, [editor, isEditable]);

  React.useEffect(() => {
    if (!editor) {
      acceptEditsRef.current = false;
      return;
    }
    const incoming = value ?? "";
    if (incoming !== lastValueRef.current && (!focusedRef.current || syncWhileFocused)) {
      writeValue(editor, incoming, false);
      lastValueRef.current = incoming;
    }
    acceptEditsRef.current = true;
  }, [editor, value, writeValue, syncWhileFocused]);

  useChipStateRefresh(editor, chipStateKey);
  useInjectedStyles("rich-chip-editor-base-styles", CHIP_BASE_CSS);
  useInjectedStyles("editor-markdown-styles", EDITOR_MARKDOWN_CSS);
  useInjectedStyles(chipStyles?.id ?? "", useChipTypeCss(chipStyles));

  const style: React.CSSProperties = {};
  if (!seamless && !singleLine) {
    style.minHeight = `${Math.max(rows * 1.5, 4)}rem`;
    if (maxRows !== undefined) {
      style.maxHeight = `${Math.max(maxRows * 1.5, 4)}rem`;
      style.overflowY = "auto";
    }
  }

  return (
    <EditorContent
      editor={editor}
      className={cn(
        seamless && "markdown-editor markdown-editor--seamless",
        !seamless && hasChip && "chip-editor",
        !seamless && !hasChip && "markdown-editor markdown-editor--boxed",
        !seamless && "border-input w-full rounded-md border bg-transparent shadow-xs",
        !seamless && "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
        !seamless && "px-3 py-2",
        disabled && "cursor-not-allowed opacity-50",
        onChipClick && "chip-clickable",
        (onChipClick || onChipHover) && "chip-interactive",
        className,
      )}
      style={style}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    />
  );
}

// ============================================================================
// Read-only renderer
// ============================================================================

export interface RichChipContentProps {
  value: string | null | undefined;
  className?: string;
  /** Configured chip node (memoize at the call site). */
  chipExtension: AnyExtension;
  /** Wire names of `chipExtension`; see {@link RichChipEditorProps.chipNode}. */
  chipNode?: ChipNodeMeta;
  /** Parse plain-text content as markdown (default true). */
  markdown?: boolean;
  /** Per-refType chip styling for this surface's chip family. */
  chipStyles?: ChipStylesConfig;
  /** Called when the user clicks a chip (chips get a pointer cursor). */
  onChipClick?: ChipClickHandler;
  /** Called when the pointer enters a chip; see
   *  {@link RichChipEditorProps.onChipHover}. */
  onChipHover?: ChipClickHandler;
  /** Called when the pointer leaves the hovered chip. */
  onChipHoverEnd?: () => void;
  /** Per-chip visual state; see {@link RichChipEditorProps.getChipState}. */
  getChipState?: ChipStateGetter;
  /** Bump when the data behind `getChipState` changes. */
  chipStateKey?: unknown;
}

/** Read-only renderer for chip documents (links open on click). */
export function RichChipContent({
  value,
  className,
  chipExtension,
  chipNode = DEFAULT_CHIP_NODE,
  markdown = true,
  chipStyles,
  onChipClick,
  onChipHover,
  onChipHoverEnd,
  getChipState,
  chipStateKey,
}: RichChipContentProps) {
  const { chipStateExtension, handleClick, handleMouseOver, handleMouseOut, handleKeyDown } =
    useChipInteraction({ onChipClick, onChipHover, onChipHoverEnd }, getChipState, chipNode);

  const editor = useEditor({
    immediatelyRender: false,
    editable: false,
    extensions: [
      ...buildChipExtensions({
        chip: chipExtension,
        openLinksOnClick: true,
        markdown,
      }),
      chipStateExtension,
    ],
    content: initialContent(value),
    editorProps: {
      // Read-only renderers are not inputs; Tiptap's default role="textbox"
      // trips axe's aria-input-field-name on every consuming surface.
      attributes: { role: "document" },
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(initialContent(value), { emitUpdate: false });
  }, [editor, value]);

  useChipStateRefresh(editor, chipStateKey);
  useInjectedStyles("rich-chip-editor-base-styles", CHIP_BASE_CSS);
  useInjectedStyles(chipStyles?.id ?? "", useChipTypeCss(chipStyles));

  if (!value) return null;

  return (
    <div
      className={cn(
        "chip-content",
        onChipClick && "chip-clickable",
        (onChipClick || onChipHover) && "chip-interactive",
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
